import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../translations';
import {
  Menu, LogOut, Settings, ChevronDown,
  Rocket, Info, HelpCircle, FileText, Shield, Mail, UserX,
  LogIn, UserPlus, X, Smartphone
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from './ui/Logo';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { language, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const userMenuRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setMenuOpen(false);
  };

  // Close menu when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        if (menuOpen) setMenuOpen(false);
        if (userMenuOpen) setUserMenuOpen(false);
      }
    };

    if (menuOpen || userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [menuOpen, userMenuOpen]);

  const dropdownVariants = {
    hidden: { opacity: 0, y: -8, scale: 0.96 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.15, ease: 'easeOut' } },
    exit: { opacity: 0, y: -4, scale: 0.98, transition: { duration: 0.1 } }
  };

  /* Fixed dropdowns: RTL-aware horizontal inset + notches; max width avoids tiny-screen overflow */
  const menuPanelSide = isRTL
    ? { left: 'max(1rem, env(safe-area-inset-left, 0px))', right: 'auto' }
    : { right: 'max(1rem, env(safe-area-inset-right, 0px))', left: 'auto' };
  const menuPanelTop = 'max(4.5rem, calc(3.5rem + env(safe-area-inset-top, 0px)))';

  const logoDestination = () => {
    if (!isAuthenticated()) return '/';
    if (user?.role === 'shadchan') return '/shadchan/dashboard';
    if (user?.role === 'single') return '/single/dashboard';
    if (user?.role === 'parent') return '/parent/dashboard';
    return '/';
  };

  const MenuItem = ({ to, href, icon: Icon, children, onClick, highlight }) => {
    const itemClass = `flex items-center gap-3 px-4 py-3 sm:py-2.5 min-h-[44px] sm:min-h-0 text-sm transition-colors rounded-lg mx-1 ${
      highlight
        ? 'text-blue-600 font-semibold bg-blue-50 hover:bg-blue-100'
        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
    }`;
    const closeMenu = () => {
      setMenuOpen(false);
      onClick?.();
    };
    return (
      <li>
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={itemClass}
            onClick={() => closeMenu()}
            style={{ textDecoration: 'none' }}
          >
            <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
            {children}
          </a>
        ) : to ? (
          <Link
            to={to}
            className={itemClass}
            onClick={() => closeMenu()}
            style={{ textDecoration: 'none' }}
          >
            <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
            {children}
          </Link>
        ) : (
          <button
            className="flex items-center gap-3 px-4 py-3 sm:py-2.5 min-h-[44px] sm:min-h-0 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 active:bg-gray-100 transition-colors rounded-lg mx-1 w-[calc(100%-8px)] border-0 bg-transparent cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              onClick?.();
            }}
          >
            <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
            {children}
          </button>
        )}
      </li>
    );
  };

  return (
    <nav className="navbar navbar-expand-lg nav-glass" style={{ color: 'inherit', direction: 'ltr' }}>
      <div className="container" style={{ direction: 'ltr', maxWidth: '1100px' }}>
        <Link className="navbar-brand d-flex align-items-center" to={logoDestination()} style={{ textDecoration: 'none' }}>
          <Logo height={36} />
        </Link>

        <div className="d-flex align-items-center gap-2">
          {!isAuthenticated() && (
            <div className="d-none d-sm-flex align-items-center gap-2 me-1">
              <Link
                to="/login"
                className="inline-flex items-center px-3 py-2 rounded-xl text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all no-underline"
              >
                {getTranslation('login', language)}
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all no-underline shadow-sm hover:shadow-md hover:-translate-y-px"
                style={{ background: 'linear-gradient(135deg, #7B2FF7, #9F44FF)' }}
              >
                {getTranslation('register', language)}
              </Link>
            </div>
          )}

          {/* Direct settings button for shadchan */}
          {isAuthenticated() && user?.role === 'shadchan' && (
            <Link
              to="/shadchan/settings"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all border-0 bg-transparent cursor-pointer"
              style={{ textDecoration: 'none' }}
            >
              <Settings className="w-4 h-4" />
              <span className="d-none d-md-inline">Settings</span>
            </Link>
          )}

          {/* User dropdown for authenticated users */}
          {isAuthenticated() && (
            <div className="position-relative" ref={userMenuRef}>
              <button
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all border-0 bg-transparent cursor-pointer"
                type="button"
                onClick={(e) => { e.stopPropagation(); setUserMenuOpen(!userMenuOpen); }}
              >
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-white text-xs font-bold">
                  {(user?.firstName?.[0] || '').toUpperCase()}
                </div>
                <span className="d-none d-md-inline text-gray-800">{user?.firstName}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {userMenuOpen && createPortal(
                <>
                  <div
                    style={{ position: 'fixed', inset: 0, zIndex: 9997 }}
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <AnimatePresence>
                    <motion.div
                      initial="hidden" animate="visible" exit="exit" variants={dropdownVariants}
                      className="bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 overflow-hidden"
                      style={{ position: 'fixed', top: menuPanelTop, minWidth: '200px', maxWidth: 'min(360px, calc(100vw - 1.5rem))', zIndex: 9998, ...menuPanelSide }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900">{user?.firstName} {user?.lastName}</p>
                        <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                      </div>
                      <div className="py-1">
                        <button
                          className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors border-0 bg-transparent cursor-pointer"
                          onClick={(e) => { e.stopPropagation(); setUserMenuOpen(false); handleLogout(); }}
                        >
                          <LogOut className="w-4 h-4" />
                          {getTranslation('logout', language)}
                        </button>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </>,
                document.body
              )}
            </div>
          )}

          {/* Hamburger menu */}
          <div className="position-relative" ref={menuRef}>
            <button
              className="inline-flex items-center justify-center w-11 h-11 sm:w-10 sm:h-10 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200 transition-all border-0 bg-transparent cursor-pointer touch-manipulation"
              type="button"
              onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
              aria-label="Menu"
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            {menuOpen && createPortal(
              <>
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(4px)' }}
                  onClick={() => setMenuOpen(false)}
                />
                <AnimatePresence>
                  <motion.ul
                    initial="hidden" animate="visible" exit="exit" variants={dropdownVariants}
                    className="bg-white rounded-xl shadow-xl border border-gray-100 py-2 overflow-hidden list-unstyled mb-0"
                    style={{ position: 'fixed', top: menuPanelTop, minWidth: 'min(240px, calc(100vw - 2rem))', maxWidth: 'calc(100vw - 1.5rem)', zIndex: 9999, ...menuPanelSide }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <MenuItem to="/getting-started" icon={Rocket}>{getTranslation('gettingStarted', language)}</MenuItem>

                    <MenuItem to="/about" icon={Info}>{getTranslation('about', language)}</MenuItem>
                    
                    {isAuthenticated() && (user?.role === 'single' || user?.role === 'parent') && (
                      <MenuItem 
                        to="/subscription" 
                        icon={Settings}
                        highlight={!user?.isPro}
                      >
                        {user?.isPro
                          ? getTranslation('navSubscriptionManage', language)
                          : getTranslation('navSubscriptionSetup', language)}
                      </MenuItem>
                    )}
                    
                    <MenuItem to="/faq" icon={HelpCircle}>{getTranslation('faq', language)}</MenuItem>
                    <MenuItem to="/mobile-app" icon={Smartphone}>{getTranslation('navMobileApp', language)}</MenuItem>

                    <li className="my-1.5 mx-3 h-px bg-gray-100" />
                    
                    <MenuItem to="/terms" icon={FileText}>{getTranslation('terms', language)}</MenuItem>
                    <MenuItem to="/privacy" icon={Shield}>{getTranslation('privacy', language)}</MenuItem>
                    <MenuItem to="/contact" icon={Mail}>{getTranslation('contact', language)}</MenuItem>
                    <MenuItem to="/delete-account" icon={UserX}>{getTranslation('deleteAccountData', language)}</MenuItem>
                    
                    <li className="my-1.5 mx-3 h-px bg-gray-100" />
                    
                    {!isAuthenticated() ? (
                      <>
                        <MenuItem to="/login" icon={LogIn}>{getTranslation('login', language)}</MenuItem>
                        <MenuItem to="/register" icon={UserPlus}>{getTranslation('register', language)}</MenuItem>
                      </>
                    ) : (
                      <li>
                        <button 
                          className="flex items-center gap-3 px-4 py-3 sm:py-2.5 min-h-[44px] sm:min-h-0 text-sm text-red-600 hover:bg-red-50 active:bg-red-100 transition-colors rounded-lg mx-1 w-[calc(100%-8px)] border-0 bg-transparent cursor-pointer touch-manipulation"
                          onClick={(e) => { e.stopPropagation(); handleLogout(); }}
                        >
                          <LogOut className="w-4 h-4" />
                          {getTranslation('logout', language)}
                        </button>
                      </li>
                    )}
                  </motion.ul>
                </AnimatePresence>
              </>,
              document.body
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
